import { useState } from 'react';
import { useCurrentUser } from '../../../../../shared/features/session/components/CurrentUserProvider';
import { useSubmission } from '../../../../../shared/hooks/use-submission';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { useConfirmation } from '../../../hooks/use-confirmation';
import { confirmTotpEnrollment, disableTotp, startTotpEnrollment } from '../security.api';

export type EnrollmentState = { phase: 'idle' } | { phase: 'secret'; secret: string; qrCodeDataUrl: string } | { phase: 'recovery-codes'; codes: string[] };

/**
 * The enrolment wizard: idle → secret + QR shown → a code is entered → recovery codes shown exactly once (unless email
 * codes already issued a set, which still stands) → back to idle, enabled. Re-enrolling and turning off go through
 * "Confirm it's you" when the session needs it.
 */
export function useTotpEnrollment() {
  const { state, setUser } = useCurrentUser();
  const toast = useToast();
  const confirmation = useConfirmation();
  const [enrollment, setEnrollment] = useState<EnrollmentState>({ phase: 'idle' });
  const { error, submitting, submit, clearError } = useSubmission();

  const user = state.status === 'authenticated' ? state.user : undefined;

  async function start() {
    const started = await submit(
      () => confirmation.confirmed('set up the authenticator app', startTotpEnrollment),
      (err) => apiErrorMessage(err) ?? 'Could not start enrolment — try again.',
    );
    if (started) setEnrollment({ phase: 'secret', secret: started.secret, qrCodeDataUrl: started.qrCodeDataUrl });
  }

  /** Called once the user has confirmed they saved the recovery codes — closes the wizard. */
  function finish() {
    if (user) setUser({ ...user, totpEnabled: true });
    setEnrollment({ phase: 'idle' });
    toast.success('Authenticator app turned on');
  }

  async function verify(code: string) {
    const result = await submit(
      () => confirmTotpEnrollment(code),
      () => 'Incorrect code. Check your authenticator app and try again.',
    );
    if (!result) return;
    if (result.recoveryCodes.length === 0) finish();
    else setEnrollment({ phase: 'recovery-codes', codes: result.recoveryCodes });
  }

  async function disable() {
    const isDisabled = await submit(
      () => confirmation.confirmed('turn off the authenticator app', () => disableTotp().then(() => true)),
      (err) => apiErrorMessage(err) ?? "Couldn't turn off the authenticator app. Try again.",
    );
    if (!isDisabled) return;
    if (user) setUser({ ...user, totpEnabled: false });
    toast.success('Authenticator app turned off');
  }

  function cancel() {
    setEnrollment({ phase: 'idle' });
    clearError();
  }

  return { enrollment, error, submitting, confirmation, start, verify, finish, disable, cancel };
}
